import { Button, Popconfirm, Switch, TableColumnsType, Tooltip } from 'antd';
import { Note } from 'sb/models/DealDynamicsSupplier';
import { useMemo } from 'react';
import moment from 'moment/moment';
import { FieldSorter } from 'sb/shared/Helpers';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { useScopes } from 'hooks/useScopes';

interface Props {
  hideCategoryColumn: boolean;
  onIsAcceptedChange: (id: number, checked: boolean) => void;
  onConfirmDelete: (id: number) => void;
  onEditClick: (rec: Note) => void;
}

export const DealDynamicNotesSupplierTableColumns = ({
  hideCategoryColumn,
  onIsAcceptedChange,
  onConfirmDelete,
  onEditClick
}: Props) => {
  const { aclCheck } = useScopes();

  const columns: TableColumnsType<Note> = useMemo(
    () => [
      {
        title: 'Dynamic',
        dataIndex: 'dynamic',
        sorter: (a: Note, b: Note) => FieldSorter(a.deal_dynamic?.name, b.deal_dynamic?.name),
        sortDirections: ['ascend', 'descend'],
        render: (_, rec: Note) => rec.deal_dynamic?.name
      },
      {
        title: 'Product Category',
        dataIndex: 'category',
        sorter: (a: Note, b: Note) => FieldSorter(a.supplier_category?.category, b.supplier_category?.category),
        sortDirections: ['ascend', 'descend'],
        render: (_, rec: Note) => rec.supplier_category?.category || '--'
      },
      {
        title: 'Note',
        dataIndex: 'note',
        sorter: (a: Note, b: Note) => FieldSorter(a.note, b.note),
        sortDirections: ['ascend', 'descend'],
        render: (_, rec: Note) => (
          <>
            {rec.note}
            {aclCheck(['update:supplier_dynamics_admin']) && (
              <Button type="text" onClick={() => onEditClick(rec)} icon={<EditOutlined />} />
            )}
          </>
        )
      },
      {
        title: 'Analyst',
        dataIndex: 'user',
        sorter: (a: Note, b: Note) => FieldSorter(a.users?.first_name, b.users?.first_name),
        sortDirections: ['ascend', 'descend'],
        render: (_, rec: Note) => rec.users?.first_name || '--'
      },
      {
        title: 'Date',
        dataIndex: 'creationDate',
        sorter: (a: Note, b: Note) => FieldSorter(a.creation_date, b.creation_date),
        sortDirections: ['ascend', 'descend'],
        render: (_, rec: Note) => moment(rec.creation_date).format('ll')
      },
      {
        title: 'Accepted',
        dataIndex: 'is_released',
        align: 'center',
        render: (_, rec: Note) => (
          <Switch
            disabled={!aclCheck(['update:supplier_dynamics_admin'])}
            defaultChecked={rec.is_released}
            onChange={(checked) => onIsAcceptedChange(rec.id, checked)}
          />
        )
      },
      {
        title: '',
        dataIndex: 'actions',
        align: 'center',
        width: aclCheck(['delete:supplier_dynamics_admin']) ? 80 : 1,
        render: (_, rec: Note) => {
          if (aclCheck(['delete:supplier_dynamics_admin'])) {
            return (
              <>
                <Tooltip title="Delete note.." placement="bottom">
                  <Popconfirm
                    title="Delete analyst note?"
                    overlayStyle={{ width: 240 }}
                    placement="topLeft"
                    onConfirm={() => onConfirmDelete(rec.id)}
                  >
                    <DeleteOutlined />
                  </Popconfirm>
                </Tooltip>
              </>
            );
          } else {
            return '';
          }
        }
      }
    ],
    [aclCheck, onEditClick, onIsAcceptedChange, onConfirmDelete]
  );
  // Filter out Category column if the vendor has no product categories
  return hideCategoryColumn ? columns.filter((col: any) => col.dataIndex !== 'category') : columns;
};
